import React, { useState, useMemo } from 'react';
import './DataTable.css';

const DataTable = ({ data }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Define which columns should be treated as currency
  const currencyKeywords = ['price', 'amount', 'revenue', 'spent', 'sales', 'sale', 'cost', 'spend'];
  const isCurrencyColumn = (columnName) => {
    const name = String(columnName || '').toLowerCase();
    return currencyKeywords.some((keyword) => name.includes(keyword));
  };

  // Format data based on type detection
  const formatCellValue = (value, columnName) => {
    if (value === null || value === undefined) {
      return <span className="null-value">-</span>;
    }

    // Convert to string for analysis
    const stringValue = String(value);

    // Currency detection based solely on column name
    if (isCurrencyColumn(columnName)) {
      const numericValue = parseFloat(String(stringValue).replace(/[^0-9.\-]/g, ''));
      if (!isNaN(numericValue)) {
        return (
          <span className="currency-value">
            {numericValue.toLocaleString('en-IN', {
              style: 'currency',
              currency: 'INR',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        );
      }
    }

    // Date detection
    if (
      columnName.toLowerCase().includes('date') ||
      columnName.toLowerCase().includes('time') ||
      columnName.toLowerCase().includes('created') ||
      columnName.toLowerCase().includes('updated')
    ) {
      const date = new Date(stringValue);
      if (!isNaN(date.getTime())) {
        return (
          <span className="date-value">
            {date.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            })}
          </span>
        );
      }
    }

    // Email detection
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(stringValue)) {
      return (
        <a href={`mailto:${stringValue}`} className="email-value">
          {stringValue}
        </a>
      );
    }

    // Phone number detection
    if (/^[\+]?[\d\s\-\(\)]{10,}$/.test(stringValue)) {
      return (
        <span className="phone-value">
          {stringValue}
        </span>
      );
    }

    // URL detection
    if (/^https?:\/\//.test(stringValue)) {
      return (
        <a 
          href={stringValue} 
          target="_blank" 
          rel="noopener noreferrer"
          className="url-value"
        >
          {stringValue.length > 30 ? `${stringValue.substring(0, 30)}...` : stringValue}
        </a>
      );
    }

    // JSON object parsing (for customer data, etc.)
    if (stringValue.startsWith('{') && stringValue.endsWith('}')) {
      try {
        const parsed = JSON.parse(stringValue);
        if (parsed.name || parsed.firstName || parsed.customer_name) {
          const name = parsed.name || `${parsed.firstName || ''} ${parsed.lastName || ''}`.trim() || parsed.customer_name;
          return <span className="parsed-name">{name}</span>;
        }
      } catch (e) {
        // Fall through to default
      }
    }

    // Default text formatting
    return (
      <span className="text-value">
        {stringValue.length > 50 ? `${stringValue.substring(0, 50)}...` : stringValue}
      </span>
    );
  };

  // Get column type for styling
  const getColumnType = (columnName, sampleValue) => {
    const name = columnName.toLowerCase();

    if (isCurrencyColumn(name)) {
      return 'currency';
    }
    if (name.includes('date') || name.includes('time')) {
      return 'date';
    }
    if (name.includes('count') || name.includes('quantity')) {
      return 'number';
    }
    if (name.includes('email')) {
      return 'email';
    }
    if (name.includes('phone')) {
      return 'phone';
    }

    return 'text';
  };

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      // Handle null/undefined values
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      // Convert to appropriate types for comparison
      const aNum = parseFloat(aVal);
      const bNum = parseFloat(bVal);

      let comparison = 0;

      if (!isNaN(aNum) && !isNaN(bNum)) {
        // Numeric comparison
        comparison = aNum - bNum;
      } else {
        // String comparison
        comparison = String(aVal).localeCompare(String(bVal));
      }

      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [data, sortConfig]);

  // Handle sort
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Get column headers
  const columns = data.length > 0 ? Object.keys(data[0]) : [];

  if (!data || data.length === 0) {
    return (
      <div className="empty-table">
        <p>No data to display</p>
      </div>
    );
  }

  return (
    <div className="data-table-container">
      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th 
                  key={column}
                  className={`column-header ${getColumnType(column, data[0][column])}`}
                  onClick={() => handleSort(column)}
                >
                  <div className="header-content">
                    <span className="column-name">{column}</span>
                    <span className="sort-indicator">
                      {sortConfig.key === column ? (
                        sortConfig.direction === 'asc' ? '↑' : '↓'
                      ) : (
                        '↕'
                      )}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedData.map((row, index) => (
              <tr key={index} className="data-row">
                {columns.map((column) => (
                  <td 
                    key={column}
                    className={`data-cell ${getColumnType(column, row[column])}`}
                  >
                    {formatCellValue(row[column], column)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Table footer with summary info */}
      <div className="table-footer">
        <div className="table-summary">
          Showing {data.length} {data.length === 1 ? 'row ' : 'rows '} with {columns.length} {columns.length === 1 ? 'column' : 'columns'}
        </div>
      </div>
    </div>
  );
};

export default DataTable; 